import { FileSpreadsheet, Loader2, Upload } from 'lucide-react';

const REQUIRED_COLUMNS = ['title', 'author'];
const RECOMMENDED_COLUMNS = [
  'isbn13',
  'description',
  'language',
  'reading_age',
  'publisher',
  'source_categories',
];

const UploadStep = ({ selectedFile, loading, onFileSelect, onRunDryRun }) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-bg-surface p-6">
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-primary-50 p-3 text-primary-600">
          <Upload className="h-6 w-6" />
        </div>
        <div className="flex-1 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Upload file</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Supported formats: .xlsx, .xls, .csv. Max file size: 5 MB. Max rows per import: 100.
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <a
                href="/bulk-import-template.csv"
                download
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-text-primary hover:bg-gray-50"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Download sample CSV
              </a>
              <span className="rounded-lg bg-gray-100 px-3 py-2 text-text-secondary">
                Use the template if you are preparing the sheet from scratch.
              </span>
            </div>
          </div>

          <label className="block rounded-lg border border-dashed border-gray-300 p-6 hover:border-primary-300 hover:bg-primary-50/30">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-6 w-6 text-text-secondary" />
              <div className="flex-1">
                <div className="font-medium text-text-primary">
                  {selectedFile ? selectedFile.name : 'Choose a spreadsheet file'}
                </div>
                <div className="text-sm text-text-secondary">
                  {selectedFile ? `${Math.round(selectedFile.size / 1024)} KB selected` : 'Select a file to run a dry-run preview'}
                </div>
              </div>
            </div>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="sr-only"
              onChange={(event) => onFileSelect(event.target.files?.[0] || null)}
            />
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="text-sm font-medium text-text-primary">Required columns</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {REQUIRED_COLUMNS.map((column) => (
                  <span key={column} className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                    {column}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm text-text-secondary">
                Rows missing these fields are likely to fail validation during dry-run.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="text-sm font-medium text-text-primary">Recommended columns</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {RECOMMENDED_COLUMNS.map((column) => (
                  <span key={column} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-text-secondary">
                    {column}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm text-text-secondary">
                Including description improves enrichment quality and enables AI tag generation.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onRunDryRun}
              disabled={!selectedFile || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Run Dry-Run
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadStep;
